<script setup>
import TheSelectAdAccounts from '@shared-components/ui/selects/TheSelectAdAccounts.vue'
import {defineEmits, ref, computed, defineExpose, defineProps, onMounted, nextTick, defineModel} from 'vue'
import {useAdAccountTopUpRequestStore} from '@/views/ad-account-requests/top-up/AdAccountTopUpRequestStore'
import {useDefaultStore} from '@/stores'
import {notify} from '@kyvg/vue3-notification'
import ApiErrors from '@shared-components/ui/ApiErrors.vue'
import AlertInfoSimple from '@shared-components/ui/alerts/AlertInfoSimple.vue'
import TheRadioButton from '@shared-components/ui/TheRadioButton.vue'
import {formatSum} from '@shared/utils/helpers'
import TheSwitcher from '@shared-components/ui/inputs/TheSwitcher.vue'
import AlertLightSimple from '@shared-components/ui/alerts/AlertLightSimple.vue'
import CompanyBalanceErrorAlert from '@/views/ad-account-requests/top-up/components/CompanyBalanceErrorAlert.vue'
import DisabledAccountErrorAlert from '@shared-components/pages/requests/manage-funds/top-up/DisabledAccountErrorAlert.vue'

const {createAdAccountTransferRequest} = useAdAccountTopUpRequestStore()
const {pingBackend} = useDefaultStore()

const companyModelValue = defineModel('company', {required: true})

// Emits & props
let emit = defineEmits(['created', 'open-invoice-modal'])
const props = defineProps({
  adAccount: {
    type: Object,
  },
  isAccountSelectBySearch: {
    type: Boolean,
    default: false,
  },
})

// Refs
const defaultFormData = {
  source_entity_id: null,
  source_entity_type: null,
  destination_entity_id: null,
  destination_entity_type: null,
  amount: null,
  is_asap: false,
}
const defaultLocalAccount = {
  is_disabled: false,
  balance: null,
  title: '',
  external_id: null,
}
const defaultLocalData = {
  transfer_type: 'transfer_by_amount',
  is_transfer_full_balance: false,
  source_account: {
    ...defaultLocalAccount,
  },
  destination_account: {
    ...defaultLocalAccount,
  },
}

const formData = ref(cloneSafe(defaultFormData))
const localData = ref(cloneSafe(defaultLocalData))

const error = ref(null)
const loading = ref(false)
const selectSourceAdAccountRef = ref()
const selectDestinationAdAccountRef = ref()

// Computed
const isCompanyBalanceError = computed(() => error.value?.errors?.company_balances?.length > 0)
const sourceAfterTransferAmount = computed(() => {
  if (localData.value.source_account.balance === null) return null

  return localData.value.source_account.balance - formData.value.amount
})
const sourceAfterReachTargetBalance = computed(() => {
  if (localData.value.source_account.balance === null) return null

  if (localData.value.transfer_type === 'reach_target_balance' && formData.value.amount) {
    return formData.value.amount
  }

  return localData.value.source_account.balance
})

const destinationAfterTransferAmount = computed(() => {
  if (localData.value.destination_account.balance === null) return null

  return localData.value.destination_account.balance + payloadAmount.value
})
const canSubmit = computed(() => {
  const data = formData.value
  const local = localData.value

  if (!data.amount) {
    return false
  }

  if (local.transfer_type === 'reach_target_balance' && data.amount === local.source_account.balance) {
    return false
  }

  return (
    data.source_entity_id &&
    data.destination_entity_id &&
    local.source_account.is_disabled === false &&
    local.destination_account.is_disabled === false
  )
})
const maxAmount = computed(() => {
  const balance = Number(localData.value.source_account.balance)
  return balance > 0 ? balance : 0
})
const isAmountDisabled = computed(() => {
  if (localData.value.is_transfer_full_balance && localData.value.transfer_type === 'transfer_by_amount') {
    return true
  }

  return loading.value || maxAmount.value === 0
})
const payloadAmount = computed(() => {
  if (localData.value.transfer_type === 'reach_target_balance') {
    return localData.value.source_account.balance - formData.value.amount
  }

  return formData.value.amount
})
const disableSwapButton = computed(
  () => selectDestinationAdAccountRef.value?.loading || selectSourceAdAccountRef.value?.loading || loading.value,
)

// Methods
onMounted(() => {
  init()
})
const init = () => {
  if (props.adAccount) {
    return nextTick(() => {
      selectSourceAdAccountRef.value?.handleSearch?.(props.adAccount.external_id)
      selectDestinationAdAccountRef.value?.handleSearch?.('  ')
    })
  }

  if (companyModelValue.value) {
    reloadAdAccounts()
  }
}
const handleGetOption = (option, target) => {
  localData.value[target].balance = option.balance
  localData.value[target].is_disabled = option.is_disabled
  localData.value[target].title = option.label
  localData.value[target].external_id = option.sublabel
}
const onAdAccountChanged = (adAccount, target) => {
  formData.value[target] = adAccount?.entity_type

  if (target === 'source_entity_type') {
    formData.value.amount = null
    localData.value.is_transfer_full_balance = false
  }
}
const onAmountInput = () => {
  if (formData.value.amount > maxAmount.value) {
    formData.value.amount = maxAmount.value
    localData.value.is_transfer_full_balance = true
  }
}
const setAmount = amount => {
  if (isAmountDisabled.value) {
    return
  }

  formData.value.amount = amount

  onAmountInput()
}
const resetAccount = t => (localData.value[t] = cloneSafe(defaultLocalData[t]))

const openInvoiceModal = () => {
  emit('open-invoice-modal')
}
const getPayload = () => {
  return {
    ...cloneSafe(formData.value),
    amount: payloadAmount.value,
  }
}
const handleCreate = async () => {
  loading.value = true

  try {
    await createAdAccountTransferRequest(getPayload())

    emit('created')
    notify({type: 'success', text: 'Transfer request created!'})
    pingBackend()
  } catch (responseError) {
    error.value = responseError.data
    throw responseError
  } finally {
    loading.value = false
  }
}

const onToggleTransferFullBalance = value => {
  if (value) {
    return (formData.value.amount = maxAmount.value)
  }

  formData.value.amount = null
}

const swapAdAccounts = () => {
  const src = {
    id: formData.value.source_entity_id,
    type: formData.value.source_entity_type,
    acc: cloneSafe(localData.value.source_account),
  }

  const dst = {
    id: formData.value.destination_entity_id,
    type: formData.value.destination_entity_type,
    acc: cloneSafe(localData.value.destination_account),
  }

  localData.value.is_transfer_full_balance = false
  formData.value.source_entity_id = dst.id
  formData.value.source_entity_type = dst.type
  localData.value.source_account = cloneSafe(dst.acc)

  formData.value.destination_entity_id = src.id
  formData.value.destination_entity_type = src.type
  localData.value.destination_account = cloneSafe(src.acc)

  if (!formData.value.source_entity_id) {
    formData.value.source_entity_type = null
    resetAccount('source_account')
  }

  if (!formData.value.destination_entity_id) {
    formData.value.destination_entity_type = null
    resetAccount('destination_account')
  }

  formData.value.amount = null

  nextTick(() => {
    selectSourceAdAccountRef.value?.handleSearch?.(localData.value.source_account.external_id)
    selectDestinationAdAccountRef.value?.handleSearch?.(localData.value.destination_account.external_id)
  })
}

const reloadAdAccounts = () => {
  nextTick(() => {
    selectSourceAdAccountRef.value?.handleSearch?.('  ')
    selectDestinationAdAccountRef.value?.handleSearch?.('  ')
  })
}

const resetFormAndReloadAccounts = () => {
  formData.value = {...defaultFormData}
  localData.value = {...defaultLocalData}

  reloadAdAccounts()
}

defineExpose({handleCreate, canSubmit, resetFormAndReloadAccounts})
</script>

<template>
  <div class="alert alert-grey-with-border d-flex custom gap-8 mb-4 py-5">
    <the-radio-button
      v-model="localData.transfer_type"
      id="transfer_by_amount"
      label="Transfer by Amount"
      name="transfer_type"
      value="transfer_by_amount"
      class="custom mr-5"
    />
    <the-radio-button
      v-model="localData.transfer_type"
      id="target_balance"
      label="Reach Target Balance"
      name="transfer_type"
      value="reach_target_balance"
    />
  </div>
  <h5 class="custom fs-5 fw-semibold mb-4">Transfer From</h5>
  <div class="alert alert-grey-with-border custom py-5">
    <div class="row">
      <div class="col">
        <label class="custom fs-4 lh-1" for="transfer-request-source-ad-account">Ad Account *</label>
        <the-select-ad-accounts
          v-model="formData.source_entity_id"
          :list-by-search="isAccountSelectBySearch"
          :payload="{with_can_has_top_up: 1, company_hash: companyModelValue, purpose: 'topup'}"
          @changed="onAdAccountChanged($event, 'source_entity_type')"
          @select-option="handleGetOption($event, 'source_account')"
          @deselect="resetAccount('source_account')"
          :disabled="loading"
          id="transfer-request-source-ad-account"
          placeholder="Select Ad Account"
          ref="selectSourceAdAccountRef"
          :preselect-once="!!props.adAccount"
          required
        />
      </div>
      <div class="col-auto w-180-px pl-0">
        <label class="custom fs-4 lh-1" for="transfer-request-amount">Amount *</label>
        <div class="input-group">
          <span class="input-group-text">$</span>
          <input
            v-model.number="formData.amount"
            @input="onAmountInput"
            :class="['form-control', isCompanyBalanceError ? 'is-invalid' : '']"
            type="number"
            step="0.01"
            id="transfer-request-amount"
            placeholder="Enter Amount"
            min="0"
            :disabled="isAmountDisabled"
            :max="maxAmount"
            required
          />
        </div>
      </div>
    </div>
    <div v-if="localData.transfer_type === 'transfer_by_amount'" class="row mt-4 custom">
      <div class="col">
        <the-switcher
          v-model="localData.is_transfer_full_balance"
          label="Transfer full ad account balance"
          id="is-transfer-full-balance"
          :disabled="loading || !formData.source_entity_id"
          @change="onToggleTransferFullBalance"
        />
      </div>
    </div>
    <alert-light-simple v-if="localData.transfer_type === 'reach_target_balance'" class="custom mt-4 fs-13-px">
      Set the target balance for this account. We’ll transfer the difference from the selected source account, based on
      the current balance.
    </alert-light-simple>
    <template v-if="formData.source_entity_id">
      <alert-info-simple v-if="localData.transfer_type === 'transfer_by_amount'" class="custom mt-4 fs-13-px">
        Available for transfer:
        <span @click.prevent="setAmount(sourceAfterTransferAmount)" class="pointer">{{
          formatSum(sourceAfterTransferAmount)
        }}</span>
      </alert-info-simple>
      <alert-info-simple v-else class="custom mt-4 fs-13-px">
        Current balance:
        <span @click.prevent="setAmount(localData.source_account.balance)" class="pointer">{{
          formatSum(localData.source_account.balance)
        }}</span>
      </alert-info-simple>
    </template>
    <disabled-account-error-alert v-if="localData.source_account.is_disabled" class="custom mt-4" />
  </div>
  <div class="transfer-divider custom my-4">
    <div class="transfer-divider__button-wrapper">
      <button
        @click.prevent="swapAdAccounts"
        class="form-icon-btn row-height"
        type="button"
        :disabled="disableSwapButton"
      >
        <i class="fa-light fa-arrow-up-arrow-down text-muted"></i>
      </button>
    </div>
  </div>
  <h5 class="custom fs-5 fw-semibold mb-4">Transfer To</h5>

  <div class="alert alert-grey-with-border custom mb-4 py-5">
    <div class="row">
      <div class="col">
        <label class="custom fs-4 lh-1" for="transfer-destination-request-ad-account">Ad Account *</label>
        <the-select-ad-accounts
          v-model="formData.destination_entity_id"
          ref="selectDestinationAdAccountRef"
          :list-by-search="isAccountSelectBySearch"
          :payload="{with_can_has_top_up: 1, company_hash: companyModelValue, purpose: 'topup'}"
          @changed="onAdAccountChanged($event, 'destination_entity_type')"
          @select-option="handleGetOption($event, 'destination_account')"
          @deselect="resetAccount('destination_account')"
          :disabled="loading"
          id="transfer-destination-request-ad-account"
          placeholder="Select Ad Account"
          required
        />
      </div>
    </div>

    <alert-info-simple v-if="formData.destination_entity_id" class="custom mt-4 fs-13-px"
      >Current balance: {{ formatSum(localData.destination_account.balance) }}
    </alert-info-simple>
    <disabled-account-error-alert v-if="localData.destination_account.is_disabled" class="custom mt-4" />
  </div>

  <company-balance-error-alert v-if="isCompanyBalanceError" @open-invoice-modal="openInvoiceModal" class="my-3" />
  <api-errors v-if="error && !isCompanyBalanceError" :error="error" class="mt-3" />

  <div>
    <div class="row">
      <div class="col text-end fw-semibold">Transfer amount:</div>
      <div class="col-auto w-100-px text-end fw-semibold pl-0">
        {{ formData.amount ? formatSum(formData.amount) : '-' }}
      </div>
    </div>

    <div v-if="localData.source_account.title" class="row custom mt-2">
      <div class="col text-end fs-13-px">{{ localData.source_account.title }} new balance:</div>
      <div class="col-auto w-100-px text-end fs-13-px pl-0">
        <span v-if="formData.amount">
          {{
            localData.transfer_type === 'reach_target_balance'
              ? formatSum(sourceAfterReachTargetBalance)
              : formatSum(sourceAfterTransferAmount)
          }}
        </span>
        <span v-else>-</span>
      </div>
    </div>
    <div v-if="localData.destination_account.title" class="row custom mt-2">
      <div class="col text-end fs-13-px">{{ localData.destination_account.title }} new balance:</div>
      <div class="col-auto w-100-px text-end fs-13-px pl-0">
        {{ formData.amount ? formatSum(destinationAfterTransferAmount) : '-' }}
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.transfer-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;

  &:after {
    display: block;
    content: '';
    width: 100%;
    height: 1px;
    background: #e5e7eb;
    position: absolute;
    top: calc(50% - 0.5px);
    right: 0;
    left: 0;
    z-index: -1;
  }

  .transfer-divider__button-wrapper {
    padding: 0 12px;
    background: #fff;
    z-index: 1;
  }
}
</style>
